import { ArrowRight, FileSpreadsheet } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { getDigitalAccessUsers, type DigitalAccessUser } from "@/api/digital-access";

import { Section, type DiagnosticTabProps } from "../../DiagnosticSection";
import {
  DA_DOC_TYPE_NAMES,
  countedShare,
  digitalAccessEnvelope,
  digitalAccessFrame,
  hiddenByDefault,
  subsequentDocumentItems,
  type DaItem,
  type DaTotals,
  type DigitalAccessFrame,
} from "../../selectors/digitalAccess";
import { downloadDigitalAccessWorkbook } from "../../digitalAccess/exportDigitalAccess";
import { SubsequentDocumentNote } from "../../digitalAccess/SubsequentDocumentNote";
import { NO_VALUE } from "../../sectionState";
import { formatDateOnly } from "@/lib/datetime";
import { formatCount } from "@/lib/format";

const HEAD = "px-2 py-2 font-mono text-[9px] font-medium uppercase tracking-[0.08em] text-[#7b8799]";
const CELL = "px-2 py-1.5 align-top";
const NUMBER = `${CELL} text-right font-mono tabular-nums text-[#08254c]`;

/** O slug da tela de usuários técnicos em `DIAGNOSTIC_DETAILS`, relativo ao Diagnóstico. */
const USERS_DETAIL = "technical-users";

/**
 * "Digital Access por tipo de documento" (#1346 fatia 9, EF-FUNC-005 RF-04 e
 * RF-05), numa linha própria do Ato 1.
 *
 * A EF pede esta tabela no lugar da lista do quadro de volume de documentos. Ela
 * entra ao lado, por ora: tirar a lista leva junto a NF-e e o gráfico mensal, e o
 * destino da NF-e é de outra EF (EF-FUNC-006).
 *
 * Documento de Digital Access não é dinheiro, e por isso cabe no Ato 1: o bloco e
 * o preço ficam com a BOM, no Ato 5.
 */
export function DigitalAccessCard({ sections, snapshots, validation }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const envelope = digitalAccessEnvelope(sections);
  const frame = digitalAccessFrame(sections, validation);

  return (
    <div data-testid="digital-access-card" data-state={envelope.state}>
      <Section
        name="digital_access_estimate"
        sections={sections}
        title={t("Digital Access by document type")}
        subtitle={frame ? <Period frame={frame} /> : undefined}
        insight={frame ? t("These documents feed the Digital Access sizing of the BOM in Investment.") : undefined}
        snapshots={snapshots}
        showsSampling={false}
        actions={
          frame ? (
            <div className="flex items-center gap-3">
              <ExportButton frame={frame} />
              <UsersLink />
            </div>
          ) : undefined
        }
      >
        {frame && <FrameBody frame={frame} />}
      </Section>
    </div>
  );
}

/**
 * "Exportar para Excel" (RF-05, #1346 fatia 8): itens e usuários numa planilha.
 * O quadro não carrega a lista de usuários, então ela é buscada no clique; sem
 * ela (falha ou projeto fora da rota), a planilha sai só com os itens.
 */
function ExportButton({ frame }: { frame: DigitalAccessFrame }) {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const [busy, setBusy] = useState(false);

  const exportWorkbook = async () => {
    setBusy(true);
    let users: DigitalAccessUser[] = [];
    try {
      if (projectId) users = (await getDigitalAccessUsers(projectId)).users;
    } catch {
      users = [];
    }
    try {
      await downloadDigitalAccessWorkbook(frame, users, t);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      data-testid="da-card-export-excel"
      disabled={busy}
      onClick={() => void exportWorkbook()}
      className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[#08254c] hover:underline disabled:opacity-50"
    >
      <FileSpreadsheet aria-hidden className="h-3 w-3" />
      {t("Export to Excel")}
    </button>
  );
}

/**
 * "Ver usuários técnicos" (RF-06): onde o consultor decide quem conta. Só existe
 * com dado, e por isso só ele usa rota: o quadro fechado renderiza fora de roteador.
 */
function UsersLink() {
  const { t } = useTranslation();
  return (
    <Link
      to={USERS_DETAIL}
      data-testid="da-users-link"
      className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[#08254c] hover:underline"
    >
      {t("See technical users")}
      <ArrowRight aria-hidden className="h-3 w-3" />
    </Link>
  );
}

function Period({ frame }: { frame: DigitalAccessFrame }) {
  const { t } = useTranslation();
  if (frame.periodFrom && frame.periodTo) {
    return (
      <>
        {t("Documents created in the production environment from {{from}} to {{to}}", {
          // Data sem hora: `formatDate` leria meia-noite UTC e, a oeste de UTC,
          // mostraria o dia anterior.
          from: formatDateOnly(frame.periodFrom),
          to: formatDateOnly(frame.periodTo),
        })}
      </>
    );
  }
  return <>{t("Documents created in the production environment")}</>;
}

/**
 * O número com `+` quando a contagem dele bateu no teto, ou o traço quando não há.
 * O `+` vai só no número que cortou (#1521): no 4101 o total lançado pode cortar
 * com a estimativa inteira.
 */
function amount(value: number | undefined, floor: boolean): string {
  if (value === undefined) return NO_VALUE;
  return `${formatCount(Math.round(value))}${floor ? "+" : ""}`;
}

function FrameBody({ frame }: { frame: DigitalAccessFrame }) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const userTypes = frame.countedUserTypes.join(", ");
  const hidden = frame.groups.flatMap((group) => group.items).filter(hiddenByDefault).length;

  return (
    <>
      <div className="overflow-x-auto">
        <table data-testid="da-table" className="w-full min-w-[760px] border-collapse text-[11px]">
          <thead>
            <tr className="border-b border-[#e8ebf0] bg-[#f7f9fc] text-left">
              <th className={HEAD}>#</th>
              <th className={HEAD}>{t("Document type")}</th>
              <th className={HEAD}>{t("Item (SAP ID)")}</th>
              <th className={HEAD}>{t("Table")}</th>
              <th className={`${HEAD} text-right`}>{t("Total posted")}</th>
              <th className={`${HEAD} text-right`}>
                {t("Created by integration users ({{types}})", { types: userTypes })}
              </th>
              <th className={`${HEAD} text-right`}>{t("Factor")}</th>
              <th className={`${HEAD} text-right`}>{t("Digital Access documents")}</th>
            </tr>
          </thead>
          <tbody>
            {frame.groups.map((group) => {
              const visible = showAll
                ? group.items
                : group.items.filter((item) => !hiddenByDefault(item));
              return visible.map((item, index) => (
                <ItemRow
                  key={item.itemId}
                  item={item}
                  first={index === 0}
                  position={group.position}
                />
              ));
            })}
            {hidden > 0 && (
              <tr className="border-b border-[#e8ebf0]">
                <td colSpan={8} className={`${CELL} text-[#7b8799]`}>
                  <button
                    type="button"
                    data-testid="da-show-all"
                    onClick={() => setShowAll((value) => !value)}
                    className="hover:text-[#08254c] hover:underline"
                  >
                    {showAll
                      ? t("Hide the {{count}} items without volume", { count: hidden })
                      : t("+ {{count}} items without volume (hidden) · Show all", { count: hidden })}
                  </button>
                </td>
              </tr>
            )}
            <TotalRow totals={frame.totals} />
          </tbody>
        </table>
      </div>

      {frame.decisions && <Decisions frame={frame} />}

      <SubsequentDocumentNote
        items={subsequentDocumentItems(frame)}
        testId="da-subsequent-note"
        className="mt-3 text-[11px] leading-snug text-[#596b85]"
      />

      <p data-testid="da-source-note" className="mt-3 text-[11px] leading-snug text-[#7b8799]">
        {t(
          "Source: user type of each document's creator (USR02-USTYP: {{types}}). Estimate for sizing: the official count is the SAP measurement (USMM/LAW) or the SAP Digital Access evaluation.",
          { types: userTypes },
        )}
        {frame.ledgerRestricted && (
          <>
            {" "}
            {t("In the financial document (4101), only the leading ledger posted from FI counts.")}
          </>
        )}
        {!frame.complete && (
          <>
            {" "}
            {frame.estimateComplete
              ? t("Only the total posted could not be counted in full (marked with +): the Digital Access documents are complete.")
              : t("Part of the items could not be counted in full: the numbers marked with + are a floor.")}
          </>
        )}
      </p>
    </>
  );
}

function ItemRow({ item, first, position }: { item: DaItem; first: boolean; position: number }) {
  const { t } = useTranslation();
  const share = countedShare(item);
  const notApplicable = !item.applicable;

  return (
    <tr data-testid={`da-item-${item.itemId}`} className="border-b border-[#eef1f5]">
      <td className={`${CELL} font-mono text-[#7b8799]`}>{first ? position : ""}</td>
      <td className={`${CELL} text-[#08254c]`}>
        {first && <span data-testid={`da-type-${item.docType}`}>{DA_DOC_TYPE_NAMES[item.docType]}</span>}
      </td>
      <td className={`${CELL} text-[#08254c]`}>{`${item.name} (${item.itemId})`}</td>
      <td className={`${CELL} font-mono text-[10px] font-semibold text-[#08254c]`}>
        {item.tables.join(" / ") || NO_VALUE}
      </td>
      <td data-testid="da-total" className={NUMBER}>
        {notApplicable ? (
          <span className="font-sans text-[#7b8799]">{t("Not applicable")}</span>
        ) : (
          amount(item.total, item.totalFloor)
        )}
      </td>
      <td className={NUMBER}>
        <span data-testid="da-counted">{notApplicable ? NO_VALUE : amount(item.counted, item.estimateFloor)}</span>
        {share !== null && (
          <span data-testid="da-counted-share" className="ml-1.5 text-[10px] text-[#7b8799]">
            {share}
          </span>
        )}
      </td>
      <td data-testid="da-factor" className={NUMBER}>
        {item.factor !== undefined ? formatCount(item.factor, { decimals: 1 }) : NO_VALUE}
      </td>
      <td data-testid="da-documents" className={NUMBER}>
        {notApplicable ? NO_VALUE : amount(item.daDocuments, item.estimateFloor)}
      </td>
    </tr>
  );
}

function TotalRow({ totals }: { totals: DaTotals }) {
  const { t } = useTranslation();
  return (
    <tr data-testid="da-total-row" className="bg-[#f7f9fc] font-semibold">
      <td className={CELL} />
      <td colSpan={3} className={`${CELL} text-[#08254c]`}>
        {t("Estimated Digital Access total")}
      </td>
      <td data-testid="da-total" className={NUMBER}>
        {amount(totals.total, totals.totalFloor)}
      </td>
      <td data-testid="da-counted" className={NUMBER}>
        {amount(totals.counted, totals.estimateFloor)}
      </td>
      <td className={CELL} />
      <td data-testid="da-documents" className={NUMBER}>
        {t("{{value}} / year", { value: amount(totals.daDocuments, totals.estimateFloor) })}
      </td>
    </tr>
  );
}

/** As decisões por usuário que já mexeram no quadro, ditas em uma linha. */
function Decisions({ frame }: { frame: DigitalAccessFrame }) {
  const { t } = useTranslation();
  const decisions = frame.decisions;
  if (!decisions) return null;
  const { excluded, included, awaitingClient } = decisions;
  if (excluded + included + awaitingClient === 0) return null;
  return (
    <p data-testid="da-decisions" className="mt-3 text-[11px] leading-snug text-[#596b85]">
      {t(
        "User decisions applied: {{excluded}} excluded, {{included}} included, {{awaiting}} to confirm with the client.",
        { excluded, included, awaiting: awaitingClient },
      )}
    </p>
  );
}
